import { Fragment, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import ComingSoonTooltip from '@/components/ui/custom/dataDisplay/ComingSoonTooltip';
import { Badge } from '@/components/ui/daisy';
import { Colors } from '@/components/ui/types';
import { ClassNameComponent } from '@/utils/ClassNameComponent';

export type ShortcutChipColor = Extract<Colors, 'primary' | 'secondary'>;

export type ShortcutChipClassnames = Pick<
  ClassNameComponent,
  'base' | 'label' | 'icon'
> & {
  badge?: string;
};

export type ShortcutChipProps = {
  icon: ReactNode;
  label: string;
  href?: string;
  color?: ShortcutChipColor;
  badge?: ReactNode;
  badgeLabel?: string;
  classNames?: ShortcutChipClassnames;
  testId?: string;
  describedBy?: string;
};

const activeChipClassNames: Record<ShortcutChipColor, string> = {
  primary: 'border-primary bg-primary/10',
  secondary: 'border-secondary bg-secondary/10',
};

const activeContentClassNames: Record<ShortcutChipColor, string> = {
  primary: 'group-aria-[current=page]:text-primary',
  secondary: 'group-aria-[current=page]:text-secondary',
};

export default function ShortcutChip({
  icon,
  label,
  href,
  color = 'secondary',
  badge,
  badgeLabel,
  classNames,
  testId,
  describedBy,
}: ShortcutChipProps) {
  const chipClassName = (isActive: boolean) =>
    twMerge(
      'group flex w-full flex-col gap-1 rounded-2xl border border-transparent bg-secondary/10 p-2',
      classNames?.base,
      isActive && activeChipClassNames[color],
    );

  const chipProps = {
    'data-testid': testId,
    'aria-describedby': describedBy,
  };

  return (
    <ComingSoonTooltip disabled={!href} className="w-full">
      <ConditionalWrapper
        condition={!!href}
        wrapper={
          <NavLink
            to={href ?? ''}
            className={({ isActive }) => chipClassName(isActive)}
            {...chipProps}
          />
        }
        fallback={
          <span className={chipClassName(false)} {...chipProps} aria-disabled />
        }
      >
        <Fragment>
          <span className="flex min-h-[34px] items-start justify-between gap-2">
            <span
              className={twMerge(
                'size-6 shrink-0 text-base-content',
                activeContentClassNames[color],
                classNames?.icon,
              )}
            >
              {icon}
            </span>
            <ConditionalRenderer condition={!!badge}>
              <Badge
                size="xs"
                color="warning"
                aria-label={badgeLabel}
                className={twMerge(
                  'px-1 text-base-content font-semibold h-4 min-w-4',
                  classNames?.badge,
                )}
              >
                {badge}
              </Badge>
            </ConditionalRenderer>
          </span>
          <span
            className={twMerge(
              'truncate text-xs leading-5 text-base-content',
              activeContentClassNames[color],
              classNames?.label,
            )}
          >
            {label}
          </span>
        </Fragment>
      </ConditionalWrapper>
    </ComingSoonTooltip>
  );
}
